import React, { useState, useEffect, useRef } from 'react';
import { createChart, CandlestickSeries, LineSeries, PriceLine, createSeriesMarkers, LastPriceAnimationMode } from 'lightweight-charts';
import { Layout, Menu, Button, Select, Input, Card, List, Typography, Space, Divider, Statistic, Row, Col, Radio } from 'antd';
import { 
  BarChartOutlined, 
  ArrowUpOutlined, 
  ArrowDownOutlined, 
  SearchOutlined, 
  BellOutlined,
  WalletOutlined,
  SettingOutlined,
  LineChartOutlined,
  DollarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import api from '../api';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import './TradeRoom.css'; // Import the CSS file
import CustomAmountInput from './CustomAmountInput';
import CustomExpirationInput from './CustomExpirationInput';
import { ReactComponent as ArrowUpIcon } from '../assets/ArrowUp.svg';
import { ReactComponent as ArrowDownIcon } from '../assets/ArrowDown.svg';
import TradeHistoryPanel from './TradeHistoryPanel';
import { VertLine } from '../plugins/vertical-line/vertical-line.ts';
import { ExpiringPriceAlerts } from '../plugins/expiring-price-alerts/expiring-price-alerts.ts';

const { Header, Sider, Content } = Layout;
const { Option } = Select;
const { Title, Text } = Typography;
// Экспортируем функцию обновления баланса для использования в ExpiringPriceAlerts
export let fetchUserBalanceGlobal = null;

const TradeRoom = () => {
  // Single ref for all chart-related objects
  const chartRef = useRef({
    container: null,
    chart: null,
    series: null,
    priceLines: [],
  });
  const intervalIdRef = useRef(null);
  const streamingProviderRef = useRef(null);
  
  // State for chart parameters
  const [symbol, setSymbol] = useState('BTC/USDT');
  const [resolution, setResolution] = useState('D');
  const [from, setFrom] = useState(Math.floor((Date.now() - 30 * 24 * 60 * 60 * 1000) / 1000));
  const [to, setTo] = useState(Math.floor(Date.now() / 1000));
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [stockList, setStockList] = useState([]);
  const [isLoadingStocks, setIsLoadingStocks] = useState(false);
  const [chartType, setChartType] = useState('line');
  
  // Binary Options state
  const [expirationSeconds, setExpirationSeconds] = useState(10); // 10 секунд по умолчанию
  const [betAmount, setBetAmount] = useState(50);
  const [userBalance, setUserBalance] = useState(1000);
  const [activeOptions, setActiveOptions] = useState([]);
  const [isCreatingOption, setIsCreatingOption] = useState(false);
  
  // Add this state to track chart data
  const [chartData, setChartData] = useState([]);

  // Вместо let localData = []
  const localData = useRef([]);

  // Ref для хранения примитива вертикальной линии
  const verticalLineRef = useRef(null);
  // Ref для второй вертикальной линии (на 10 секунд впереди)
  const forwardVerticalLineRef = useRef(null);

  // Ref для хранения ExpiringPriceAlerts
  const expiringPriceAlertsRef = useRef(null);

  // Ref для хранения серии whitespace (будущее)
  const futureSeriesRef = useRef(null);

  // Function to convert candlestick data to line data
  const convertToLineData = (candlestickData) => {
    if (!candlestickData || !Array.isArray(candlestickData)) {
      console.warn('Invalid candlestick data:', candlestickData);
      return [];
    }
    return candlestickData.map(candle => {
    if (!candle || typeof candle.close !== 'number') {
        console.warn('Invalid candle data:', candle);
        return { time: Date.now() / 1000, value: 0 };
      }
      return {
        time: candle.time,
        value: candle.close
      };
    });
  };

  function* getNextRealtimeUpdate(realtimeData) {
    for (const dataPoint of realtimeData) {
      yield dataPoint;
    }
    return null;
  }

  // Function to generate data for streaming
  const generateData = (basePrice, numberOfCandles = 20, updatesPerCandle = 5) => {
  };

  const startStreaming = () => {
  };

  const stopStreaming = () => {
  };

  // Function to get JWT token
  const getJwtToken = () => {
    // Replace with your method of obtaining the JWT token
    return localStorage.getItem('token');
  };

  // Вспомогательная функция для фильтрации уникальных по времени и сортировки
  function filterUniqueAndSortByTime(data) {
    const map = new Map();
    for (const item of data) {
      map.set(Number(item.time), item);
    }
    return Array.from(map.values()).sort((a, b) => Number(a.time) - Number(b.time));
  }

  // WebSocket клиент для получения рыночных данных
  useEffect(() => {
    let client;
    let unsubscribed = false;

    function handleCandle(candle) {
      if (chartType === 'candlestick') {
        // Обновляем или добавляем свечу
        const idx = localData.current.findIndex(c => c.time === candle.time);
        if (idx >= 0) {
          localData.current[idx] = candle;
        } else {
          localData.current.push(candle);
          if (localData.current.length > 100) localData.current.shift();
        }
        // Удаляем дубликаты и сортируем
        localData.current = filterUniqueAndSortByTime(localData.current);
        setChartData([...localData.current]);
        if (chartRef.current.series && chartRef.current.series.setData) {
          chartRef.current.series.setData([...localData.current]);
        }
      } else {
        // Для line используем close свечи
        localData.current.push({ time: candle.time, value: candle.close });
        if (localData.current.length > 100) localData.current.shift();
        // Удаляем дубликаты и сортируем
        localData.current = filterUniqueAndSortByTime(localData.current);
        setChartData([...localData.current]);
        if (chartRef.current.series && chartRef.current.series.setData) {
          chartRef.current.series.setData([...localData.current]);
        }
      }
    }

    const token = localStorage.getItem('token');
    client = new Client({
      webSocketFactory: () => new SockJS(api.getUri() + '/ws'),
      reconnectDelay: 5000,
      connectHeaders: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });

    client.onConnect = () => {
      client.subscribe(`/topic/market/${symbol}`, (message) => {
        if (unsubscribed) return;
        const candle = JSON.parse(message.body);
        handleCandle(candle);
      });
      client.publish({ destination: '/app/subscribe-crypto', body: symbol });
    };
    client.activate();

    return () => {
      unsubscribed = true;
      if (client) client.deactivate();
    };
  }, [symbol, chartType]);


  // Fetch user balance
  const fetchUserBalance = async () => {
    try {
      const response = await api.get('/api/options/balance', {
        params: { username: localStorage.getItem('username') }
      });
      setUserBalance(response.data);
    } catch (error) {
      console.error('Error fetching user balance:', error);
    }
  };
  // Сохраняем глобально для использования в ExpiringPriceAlerts
  fetchUserBalanceGlobal = fetchUserBalance;
  
  // Fetch active options
  const fetchActiveOptions = async () => {
    try {
      const response = await api.get('/api/options/active', {
        params: { username: localStorage.getItem('username') }
      });
      setActiveOptions(response.data);
      
      // Add markers for each option
      addAllOptionMarkers(response.data);
    } catch (error) {
      console.error('Error fetching active options:', error);
    }
  };

  // Вспомогательная функция для поиска ближайшей цены по времени
  function findClosestValue(data, targetTime) {
    if (!data || data.length === 0) return null;
    let closest = data[0];
    let minDiff = Math.abs(Number(closest.time) - targetTime);
    for (let i = 1; i < data.length; i++) {
      const diff = Math.abs(Number(data[i].time) - targetTime);
      if (diff < minDiff) {
        closest = data[i];
        minDiff = diff;
      }
    }
    const value = closest.value;
    return (typeof value === 'number' && !isNaN(value)) ? value : null;
  }

  // Create binary option
  const createBinaryOption = async (directionUp) => {
    if (isCreatingOption) return;
    setIsCreatingOption(true);
    try {
      const response = await api.post('/api/options', 
        {
          stockSymbol: symbol,
          amount: betAmount,
          expirationSeconds: expirationSeconds,
          directionUp: directionUp
        },
        {
          headers: {
            Authorization: `Bearer ${getJwtToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      fetchUserBalance();
      const newOption = response.data;
      setActiveOptions(prev => [...prev, newOption]);
      // Создаём ExpiringPriceAlert только после успешного ответа от сервера
      if (newOption) {
        // Используем время создания и экспирации из ответа, если есть
        const entryTime = newOption.creationTime;
        const expirationTime = newOption.expirationTime;
        const entryPrice = newOption.entryPrice;
        if (expiringPriceAlertsRef.current && typeof entryPrice === 'number' && !isNaN(entryPrice)) {
          expiringPriceAlertsRef.current.addExpiringAlert(
            entryPrice,
            entryTime,
            expirationTime,
            {
              crossingDirection: directionUp ? 'up' : 'down',
              title: `$${betAmount}`
            }
          );
        }
      }
    } catch (error) {
      console.error('Failed to create binary option:', error);
    } finally {
      setIsCreatingOption(false);
    }
  };

  // Универсальная функция для добавления маркеров всех опционов
  const addAllOptionMarkers = (options) => {
    
  };

  // Reset CSS styles that might interfere with chart rendering
  useEffect(() => {
    // Override conflicting CSS styles
    const container = document.getElementById('tradingview-widget-container');
    if (container) {
      container.style.position = 'relative';
      container.style.top = 'auto';
      container.style.left = 'auto';
      container.style.zIndex = 'auto';
    }
    
    document.body.style.backgroundColor = '#04060F';
    
    return () => {
      stopStreaming();
    };
  }, []);

  useEffect(() => {
    // Fetch user balance and active options initially
    fetchUserBalance();
    fetchActiveOptions();
    
    // Setup interval to refresh active options
    const optionsInterval = setInterval(() => {
      fetchActiveOptions();
      fetchUserBalance();
    }, 30000); // Every 30 seconds
    
    return () => {
      clearInterval(optionsInterval);
    };
  }, []);

  useEffect(() => {
    // Make sure the container exists before creating the chart
    if (!chartRef.current.container) return;
    
    // Create Lightweight Chart with dark theme
    chartRef.current.chart = createChart(chartRef.current.container, {
      width: chartRef.current.container.clientWidth,
      height: chartRef.current.container.clientHeight - 10,
      layout: {
        background: { type: 'solid', color: '#04060F' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: {
          color: '#1e2330',
        },
        horzLines: {
          color: '#1e2330',
        },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: true,
        borderColor: '#2b2b43',
        textColor: '#d1d4dc',
        rightOffset: 60,
        localization: {
          locale: 'en-US',
          dateFormat: 'yyyy-MM-dd',
          timeFormat: 'HH:mm:ss',
          priceFormatter: (price) => price.toFixed(2)
        }
      },
      crosshair: {
        mode: 0,
        vertLine: {
          color: '#2b2b43',
          width: 1,
          style: 0,
          labelBackgroundColor: '#131722',
        },
        horzLine: {
          color: '#2b2b43',
          width: 1,
          style: 0,
          labelBackgroundColor: '#131722',
        },
      },
      handleScroll: {
        vertTouchDrag: true,
      },
    });

    // Add series based on chart type
    if (chartType === 'candlestick') {
      chartRef.current.series = chartRef.current.chart.addSeries(CandlestickSeries, {
        upColor: '#28A79B',
        downColor: '#E10000',
        borderVisible: false,
        wickUpColor: '#28A79B',
        wickDownColor: '#E10000'
      });
      expiringPriceAlertsRef.current = null;
      // Безопасное удаление futureSeries если был
      if (futureSeriesRef.current && chartRef.current.chart) {
        try {
          chartRef.current.chart.removeSeries(futureSeriesRef.current);
        } catch (e) {
          // ignore if already removed
        }
        futureSeriesRef.current = null;
      }
    } else {
      chartRef.current.series = chartRef.current.chart.addSeries(LineSeries, {
        color: '#8103FC',
        lineWidth: 2,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 4,
        lastPriceAnimation: LastPriceAnimationMode.Continuous,
      });
      expiringPriceAlertsRef.current = new ExpiringPriceAlerts(chartRef.current.series, { interval: 1, onExpire: fetchUserBalance });

      // Безопасное удаление и создание futureSeries для whitespace
      if (futureSeriesRef.current && chartRef.current.chart) {
        try {
          chartRef.current.chart.removeSeries(futureSeriesRef.current);
        } catch (e) {
          // ignore if already removed
        }
      }
      futureSeriesRef.current = chartRef.current.chart.addSeries(LineSeries, {
        color: 'rgba(0,0,0,0)',
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        lastPriceAnimation: LastPriceAnimationMode.Continuous,
      });
    }

    // WebSocket теперь сам обновляет данные, старт стриминга не нужен
    
    // Handle window resize
    const handleResize = () => {
      if (chartRef.current.chart) {
        chartRef.current.chart.applyOptions({
          width: chartRef.current.container.clientWidth,
          height: chartRef.current.container.clientHeight
        });
      }
    };
    
    window.addEventListener('resize', handleResize);

    // Добавляем очистку markerSeries при смене типа графика
    if (chartRef.current.markerSeries) {
      try {
        chartRef.current.markerSeries.setMarkers([]);
        chartRef.current.markerSeries.setData([]); // если вдруг setData вызывался
      } catch (e) {
        // игнорируем ошибку, если метод не поддерживается
      }
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      stopStreaming();
      if (chartRef.current.chart) {
        chartRef.current.chart.remove();
      }
    };
  }, [chartType]);
  
  // Fetch stocks on component mount
  useEffect(() => {
    fetchStockList();
  }, []);

  // Helper function to safely format dates
  const formatDateTime = (dateString) => {
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Pending...";
      }
      
      // Format the time
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      
      return `${hours}:${minutes}:${seconds}`;
    } catch (e) {
      console.error("Error formatting date:", e);
      return "Pending...";
    }
  };
  // Fetch available stocks
  const fetchStockList = async () => {
    setIsLoadingStocks(true);
    try {
      const response = await api.get('/api/stocks', {
        params: { exchange: 'US' }
      });
      setStockList(response.data);
    } catch (error) {
      console.error('Error fetching stock list:', error);
      // Use some default stocks if API fails
      setStockList([
        { symbol: 'AAPL', name: 'Apple Inc.' },
        { symbol: 'MSFT', name: 'Microsoft Corporation' },
        { symbol: 'GOOGL', name: 'Alphabet Inc.' },
        { symbol: 'AMZN', name: 'Amazon.com Inc.' },
        { symbol: 'TSLA', name: 'Tesla, Inc.' }
      ]);
    } finally {
      setIsLoadingStocks(false);
    }
  };

  const [historyLoaded, setHistoryLoaded] = useState(false);

  const applySettings = async () => {
    if (chartRef.current.chart) {
      setIsLoading(true);
      setHistoryLoaded(false); // Сбросить флаг перед загрузкой истории
      try {
        stopStreaming();
        await fetchUserBalance();
        await fetchActiveOptions();
        // Получаем секундные бары через REST
        const response = await api.get('/api/crypto-seconds', { params: { symbols: symbol } });
        const bars = response.data || [];
        let data = [];
        if (chartType === 'candlestick') {
          data = bars.map(bar => ({
            time: bar.time,
            open: bar.open,
            high: bar.high,
            low: bar.low,
            close: bar.close
          }));
        } else {
          data = bars.map(bar => ({
            time: bar.time,
            value: bar.close
          }));
        }
        localData.current = data; // <--- сохраняем историю в localData
        localData.current = filterUniqueAndSortByTime(localData.current);
        setChartData([...localData.current]);
        if (chartRef.current.series && chartRef.current.series.setData) {
          chartRef.current.series.setData([...localData.current]);
        }
        // Масштабируем график под новый актив
        if (chartRef.current.chart && chartRef.current.chart.timeScale) {
          chartRef.current.chart.timeScale().fitContent();
        }
        setHistoryLoaded(true); // История загружена, можно добавлять realtime
      } catch (e) {
        console.error('Ошибка загрузки секундных данных:', e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle chart type change
  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
    applySettings();
  };

  // Clean up function for useEffect
  useEffect(() => {
    return () => {
      // Remove all price lines when component unmounts
      if (chartRef.current.priceLines) {
        chartRef.current.priceLines.forEach(line => {
          if (line && typeof line.remove === 'function') {
            line.remove();
          }
        });
      }
    };
  }, []);

  // Функция для фильтрации и валидации свечей
  function filterValidCandles(candles) {
    return candles.filter(c =>
      typeof c.open === 'number' &&
      typeof c.high === 'number' &&
      typeof c.low === 'number' &&
      typeof c.close === 'number' &&
      c.time !== undefined
    );
  }

  // При загрузке stockList выбираем первый актив как активный и вызываем applySettings
  useEffect(() => {
    if (stockList.length > 0) {
      // Проверяем, есть ли среди загруженных активов тот, чей symbol совпадает с текущим
      const found = stockList.find(stock => stock.symbol === symbol);
      if (found) {
        applySettings();
      }
    }
    // eslint-disable-next-line
  }, [stockList]);

  // Подключение и обновление вертикальной линии через Series Primitive
  useEffect(() => {
    if (!chartRef.current.series || !chartData.length) return;
    const lastTime = chartData[chartData.length - 1]?.time;
    if (!lastTime) return;
    // Основная линия (текущий бар)
    if (!verticalLineRef.current) {
      verticalLineRef.current = new VertLine(
        chartRef.current.chart,
        chartRef.current.series,
        lastTime,
        {
          color: '#9B7DFF',
          width: 2,
          labelText: '',
          labelBackgroundColor: '#9B7DFF',
          labelTextColor: 'white',
          showLabel: false,
          dashed: true
        }
      );
      chartRef.current.series.attachPrimitive(verticalLineRef.current);
    } else {
      verticalLineRef.current._time = lastTime;
      verticalLineRef.current.updateAllViews();
    }
    // Вторая линия (на expirationSeconds секунд впереди)
    const forwardTime = lastTime + expirationSeconds;
    if (!forwardVerticalLineRef.current) {
      forwardVerticalLineRef.current = new VertLine(
        chartRef.current.chart,
        chartRef.current.series,
        forwardTime,
        {
          color: '#9B7DFF',
          width: 2,
          labelText: '',
          labelBackgroundColor: '#9B7DFF',
          labelTextColor: 'white',
          showLabel: false
        }
      );
      chartRef.current.series.attachPrimitive(forwardVerticalLineRef.current);
    } else {
      forwardVerticalLineRef.current._time = forwardTime;
      forwardVerticalLineRef.current.updateAllViews();
    }
    // Принудительно обновляем серию, чтобы вызвать перерисовку графика
    if (chartRef.current.series && chartRef.current.series.setData) {
      chartRef.current.series.setData([...chartData]);
    }
  }, [chartData, chartRef.current.series, expirationSeconds]);

  // Функция для обновления futureSeries (whitespace на 2 минуты вперёд)
  function updateFutureSeries() {
    if (!futureSeriesRef.current || !chartRef.current.series) return;
    const data = chartRef.current.series._data || [];
    let lastTime = data.length > 0 ? data[data.length - 1].time : Math.floor(Date.now() / 1000);
    const now = Math.floor(Date.now() / 1000);
    const maxTime = Math.max(now, lastTime) + 120; // 2 минуты вперёд
    const whitespace = [];
    for (let t = lastTime + 1; t <= maxTime; t += 1) {
      whitespace.push({ time: t }); // whitespace data
    }
    futureSeriesRef.current.setData(whitespace);
  }

  // После любого setData основной серии (например, в applySettings или при обновлении данных)
  // и после создания опциона
  useEffect(() => {
    if (futureSeriesRef.current && chartRef.current.series) {
      updateFutureSeries();
    }
  }, [chartData, chartType]);

  return (
    <div className="trade-room-page">
      <Header className="toolbar">
        <Space className="toolbar-left">
          <Select
            value={symbol}
            onChange={setSymbol}
            loading={isLoadingStocks}
            style={{ width: 200 }}
            className="symbol-select"
          >
            {isLoadingStocks ? (
              <Option value="">Loading...</Option>
            ) : (
              stockList.map((stock) => (
                <Option key={stock.symbol} value={stock.symbol}>
                  {stock.symbol} - {stock.name}
                </Option>
              ))
            )}
          </Select>
          <Button 
            type="primary"
            onClick={applySettings}
            loading={isLoading}
            className="apply-button"
          >
            Apply
          </Button>
          <Radio.Group 
            value={chartType} 
            onChange={handleChartTypeChange}
            buttonStyle="solid"
            className="chart-type-switcher"
          >
            <Radio.Button value="candlestick">Candles</Radio.Button>
            <Radio.Button value="line">Line</Radio.Button>
          </Radio.Group>
        </Space>
        <Space className="toolbar-right custom-toolbar-right">
          <div className="deposit-balance-block">
            <img src="/avatar.png" alt="avatar" className="deposit-avatar" />
            <span className="deposit-balance">${userBalance.toLocaleString()}</span>
          </div>
          <button className="deposit-green-btn">
            <span className="deposit-green-btn-icon">
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_5097_24777)">
                  <rect width="24" height="24" transform="translate(0.5)" fill="white" fillOpacity="0.01"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M6.22093 10.1395C6.22093 5.64419 9.86512 2 14.3605 2C18.8558 2 22.5 5.64419 22.5 10.1395C22.5 14.6349 18.8558 18.2791 14.3605 18.2791C9.86512 18.2791 6.22093 14.6349 6.22093 10.1395ZM14.3462 5.72095C14.7316 5.72095 15.0439 6.03331 15.0439 6.41862V6.64203C15.8713 6.86939 16.5515 7.49385 16.7228 8.34872C16.7985 8.72652 16.5536 9.09417 16.1758 9.16988C15.798 9.24559 15.4304 9.00069 15.3547 8.62289C15.29 8.30011 14.9215 7.94333 14.3462 7.94333C13.6908 7.94333 13.3256 8.38858 13.3256 8.74628C13.3256 9.02583 13.3985 9.11087 13.4534 9.15469C13.5442 9.2271 13.7824 9.33485 14.3462 9.33485C15.025 9.33485 15.683 9.44619 16.1784 9.82862C16.7141 10.2422 16.9184 10.8568 16.9184 11.5331C16.9184 12.2787 16.6121 12.8729 16.076 13.254C15.7684 13.4727 15.4118 13.6028 15.0439 13.6707V13.8605C15.0439 14.2458 14.7316 14.5582 14.3462 14.5582C13.9609 14.5582 13.6486 14.2458 13.6486 13.8605V13.6485C12.7683 13.4366 12.0118 12.8287 11.8182 11.9421C11.736 11.5657 11.9745 11.1939 12.351 11.1117C12.7274 11.0295 13.0992 11.268 13.1814 11.6445C13.2457 11.939 13.648 12.3361 14.3462 12.3361C14.7835 12.3361 15.0898 12.2431 15.2674 12.1168C15.4097 12.0156 15.5231 11.8595 15.5231 11.5331C15.5231 11.1376 15.4149 11.002 15.3258 10.9332C15.1964 10.8332 14.9172 10.7302 14.3462 10.7302C13.6604 10.7302 13.0394 10.6092 12.5835 10.2457C12.0917 9.85358 11.9303 9.2955 11.9303 8.74628C11.9303 7.69506 12.6977 6.90636 13.6486 6.64311V6.41862C13.6486 6.03331 13.9609 5.72095 14.3462 5.72095ZM4.61233 9.91429C4.49049 9.87065 4.3651 10.0238 4.11432 10.33C3.10538 11.5621 2.5 13.1375 2.5 14.8542C2.5 18.8007 5.69928 22 9.64578 22C11.3625 22 12.9379 21.3946 14.17 20.3857C14.4762 20.1349 14.6293 20.0095 14.5857 19.8877C14.5421 19.7658 14.3296 19.7662 13.9048 19.7669H13.9047L13.8886 19.7669C8.83213 19.7669 4.73306 15.6679 4.73306 10.6114L4.73307 10.5953C4.7338 10.1704 4.73417 9.95793 4.61233 9.91429Z" fill="#219204"/>
                </g>
                <defs>
                  <clipPath id="clip0_5097_24777">
                    <rect width="24" height="24" fill="white" transform="translate(0.5)"/>
                  </clipPath>
                </defs>
              </svg>
            </span>
            <span className="deposit-green-btn-text">Deposit</span>
          </button>
        </Space>
      </Header>
      <div className="trade-room-main">
        <div className="trade-room-chart">
          <div 
            id="tradingview-widget-container" 
            ref={el => chartRef.current.container = el} 
            className="chart-container"
          />
        </div>
        <div className="right-panel">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div className="input-group">
              <CustomAmountInput
                value={betAmount}
                onChange={e => setBetAmount(Number(e.target.value))}
                min={1}
                max={userBalance}
                placeholder="Enter amount"
                disabled={isCreatingOption || betAmount > userBalance}
              />
              <CustomExpirationInput
                value={expirationSeconds}
                onChange={e => setExpirationSeconds(Number(e.target.value))}
                min={1}
                max={3600}
                placeholder="Enter time (seconds)"
                disabled={isCreatingOption}
              />
            </div>
            <Row gutter={16} className="direction-buttons">
              <Col span={12}>
                <Button 
                  type="primary"
                  onClick={() => createBinaryOption(true)}
                  disabled={isCreatingOption || betAmount > userBalance}
                  className="up-button"
                  block
                >
                  <span className="custom-arrow" aria-hidden="true">
                    <ArrowUpIcon />
                  </span>
                  Higher
                </Button>
              </Col>
              <Col span={12}>
                <Button 
                  type="primary"
                  onClick={() => createBinaryOption(false)}
                  disabled={isCreatingOption || betAmount > userBalance}
                  className="down-button"
                  block
                >
                  <span className="custom-arrow" aria-hidden="true">
                    <ArrowDownIcon />
                  </span>
                  Lower
                </Button>
              </Col>
            </Row>
          </Space>
        </div>
      </div>
      <TradeHistoryPanel activeOptions={activeOptions} />
    </div>
  );
};

export default TradeRoom;
